import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconInfoCircle } from '#src/atoms/icons/system/iconInfoCircle.ts';
import { iconPlus } from '#src/atoms/icons/system/iconPlus.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LabelXS } from '#src/atoms/typography/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useWalletNeedsRecover } from '#src/hooks/recovery/useWalletNeedsRecover.ts';
import { LIFECYCLE, useLifecycle } from '#src/hooks/useLifecycle.ts';
import { useNeedsCreateEthWallet } from '#src/hooks/wallets/useNeedsCreateEthWallet.ts';
import { glossary } from '#src/lib/glossary.ts';

import { RedDot } from './RedDot/index.tsx';

const Root = styled(Horizontal)`
  position: relative;
  padding: var(--unit);
`;
const NewChip = styled.div`
  color: #00f3eb;
  background: rgba(0, 243, 235, 0.16);
  border-radius: var(--double-unit);
  padding: 2px var(--unit);
`;
const StyledRedDot = styled(RedDot)`
  position: absolute;
  top: var(--unit);
  right: var(--unit);
`;

type Props = {
  onClick?: () => void;
  sidebarCollapsed?: boolean;
};
export const Deposit = ({ onClick: onClickProp, sidebarCollapsed }: Props) => {
  const isLaptop = useIsLaptop();
  const { lifecycle, update: updateLifecycle } = useLifecycle();

  const onClick = () => {
    updateLifecycle(LIFECYCLE.sawDepositButton, true);
    onClickProp?.();
  };

  const { currentUser } = useCurrentUserContext();
  const walletNeedsRecover = useWalletNeedsRecover();
  const needsCreateEthWallet = useNeedsCreateEthWallet();
  const needConfirmedDevice = !currentUser?.confirmedDevice;

  const showWarning =
    !!walletNeedsRecover || !!needConfirmedDevice || !!needsCreateEthWallet;

  const sawDepositButton = lifecycle?.[LIFECYCLE.sawDepositButton];

  const showNewChip = !sawDepositButton && !showWarning;

  if (sidebarCollapsed) {
    return (
      <Root>
        {showWarning ? (
          <IconButton icon={iconInfoCircle} color="red" size="compact" />
        ) : (
          <IconButton
            color="quaternary"
            size="compact"
            icon={iconPlus}
            onClick={onClick}
          />
        )}
        {!!showNewChip && <StyledRedDot />}
      </Root>
    );
  }

  return (
    <Root>
      <Button
        fullWidth
        color={showWarning ? 'red' : 'quaternary'}
        size={isLaptop ? 'compact' : 'small'}
        startIcon={
          <FontAwesomeIcon icon={showWarning ? iconInfoCircle : iconPlus} />
        }
        onClick={onClick}
      >
        <Horizontal>
          {showWarning ? (
            <FormattedMessage defaultMessage="Action required" />
          ) : (
            <FormattedMessage defaultMessage="Deposit" />
          )}
          {!!showNewChip && (
            <NewChip>
              <LabelXS bold>
                <FormattedMessage {...glossary.new} />
              </LabelXS>
            </NewChip>
          )}
        </Horizontal>
      </Button>
    </Root>
  );
};
